import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import type { InvoiceItem } from '@/types/payments';
import type { Rate } from '@/hooks/useGetCustomerRates';
import { AddCharge } from './add-charge';
import QuickAddItems from './QuickAddItems';
import { useHasPermission } from '@/hooks/useDriverPermissions';
import { withCharge } from '@/lib/invoiceLines';

interface InvoiceItemsEditorProps {
  items: InvoiceItem[];
  onChange: (items: InvoiceItem[]) => void;
  availableRates?: Rate[];
}

const InvoiceItemsEditor: React.FC<InvoiceItemsEditorProps> = ({
  items,
  onChange,
  availableRates = [],
}) => {
  const [showAddCharge, setShowAddCharge] = useState(false);
  // The charges are part of raising the invoice: "Edit invoice line items"
  // folded into "Create invoices".
  const canEditItems = useHasPermission('invoices.create');

  const handleAddCharge = (description: string, amount: number) => {
    // Convert AddCharge data to InvoiceItem format
    const newItem: InvoiceItem = {
      description,
      quantity: 1,
      unitPrice: amount,
    };
    onChange([...items, newItem]);
    setShowAddCharge(false);
  };

  const handleQuickAddItem = (description: string, unitPrice: number) => {
    onChange(withCharge(items, { description, unitPrice }));
  };

  const handleCancelAddCharge = () => {
    setShowAddCharge(false);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const incrementQuantity = (index: number) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      quantity: newItems[index].quantity + 1,
    };
    onChange(newItems);
  };

  const decrementQuantity = (index: number) => {
    const newItems = [...items];
    const currentQuantity = newItems[index].quantity;
    if (currentQuantity > 1) {
      newItems[index] = { ...newItems[index], quantity: currentQuantity - 1 };
      onChange(newItems);
    }
  };

  const total = items.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice,
    0
  );

  // Show AddCharge component as full-screen modal
  if (showAddCharge) {
    return (
      <AddCharge onAdd={handleAddCharge} onCancel={handleCancelAddCharge} />
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div
          key={index}
          className="bg-background rounded-lg p-4 border border-gray-200 shadow-md"
        >
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <span className="block px-3 py-2 text-sm font-medium text-gray-900">
                {item.description || 'Item'}
              </span>
            </div>
            <div className="w-auto flex flex-row items-center gap-1">
              <span className="px-2 py-2 text-sm font-medium text-gray-900 text-center">
                {item.quantity}
              </span>
              {canEditItems && (
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => incrementQuantity(index)}
                    className="p-0.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded"
                    aria-label="Increase quantity"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => decrementQuantity(index)}
                    disabled={item.quantity <= 1}
                    className="p-0.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Decrease quantity"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
              )}
            </div>
            <div className="w-auto">
              <span className="block px-2 py-2 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                ${(item.unitPrice || 0).toFixed(2)}
              </span>
            </div>
            {canEditItems && (
              <button
                onClick={() => removeItem(index)}
                className="p-2 text-gray-500 hover:bg-gray-50 rounded"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Total Card */}
      {items.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-md">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-900">Total</span>
            <span className="font-semibold text-gray-900">
              ${total.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Add Line Item button - disabled when user doesn't have edit permission */}
      <button
        onClick={() => setShowAddCharge(true)}
        disabled={!canEditItems}
        className="w-full py-3 px-4 bg-background border border-gray-200 rounded-lg shadow-md text-black font-bold hover:bg-gray-50 transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background"
      >
        <Plus size={16} className="text-black font-bold" />
        Add Line Item
      </button>

      {/* Quick Add Items - shows available rates for quick selection */}
      {availableRates.length > 0 && (
        <QuickAddItems
          availableRates={availableRates}
          onAddItem={handleQuickAddItem}
          disabled={!canEditItems}
        />
      )}
    </div>
  );
};

export default InvoiceItemsEditor;
